/**
 * PlayerAnalyticsView — Per-player analytics tab for basketball.
 *
 * Renders a clickable basketball court with player dots. Clicking a player dot
 * selects that player and reveals an individual heat map plus a 14-stat summary.
 * Clicking the same player again deselects.
 */
import React, { useState, useMemo } from "react";
import { computeHeatMapData } from "../../utils/heatMapZones";
import BasketballCourtHeatMap from "./BasketballCourtHeatMap";
import type { Match, Player } from "../../types";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { PlayerStats } from "../../hooks/usePlayerStats";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const COURT_W = 94;
const COURT_H = 50;

/** Default dot positions for 5 home + 5 away starters on a basketball court layout */
const HOME_POSITIONS: [number, number][] = [
  [20, 25], // PG
  [18, 12], // SG
  [10, 25], // C
  [18, 38], // SF
  [12, 8], // PF
];

const AWAY_POSITIONS: [number, number][] = [
  [74, 25], // PG
  [76, 12], // SG
  [84, 25], // C
  [76, 38], // SF
  [82, 8], // PF
];

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface PlayerAnalyticsViewProps {
  match: Match;
  events: MatchEvent[];
  playerStats: PlayerStats[];
  t: (key: string, fallback?: string) => string;
}

/* ------------------------------------------------------------------ */
/*  Stat card helper                                                   */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  label: string;
  value: string | number;
  testId?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, testId }) => (
  <div
    className="bg-slate-800/80 rounded-lg border border-slate-700 p-3 flex flex-col items-center"
    data-testid={testId}
  >
    <span className="text-lg font-bold text-orange-400 tabular-nums">
      {value}
    </span>
    <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-1 text-center">
      {label}
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PlayerAnalyticsView: React.FC<PlayerAnalyticsViewProps> = ({
  match,
  events,
  playerStats,
  t,
}) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  /* Determine participating players (starters + subs who entered) */
  const participatingPlayers = useMemo(() => {
    const playerIdSet = new Set<string>();
    for (const ev of events) {
      if (ev.player_id) playerIdSet.add(ev.player_id);
    }

    const allPlayers = [
      ...(match.home_team.players ?? []).map((p) => ({
        ...p,
        teamSide: "home" as const,
      })),
      ...(match.away_team.players ?? []).map((p) => ({
        ...p,
        teamSide: "away" as const,
      })),
    ];

    // Include starters + any player who has events
    return allPlayers.filter((p) => p.is_starter || playerIdSet.has(p.id));
  }, [match, events]);

  /* Get position for a player dot on the court */
  const getPlayerPosition = (
    player: Player & { teamSide: "home" | "away" },
    index: number,
  ): [number, number] => {
    const positions =
      player.teamSide === "home" ? HOME_POSITIONS : AWAY_POSITIONS;
    return positions[index % positions.length];
  };

  /* Selected player stats (fall back to zero-value stats if no events yet) */
  const selectedStats = useMemo(() => {
    if (!selectedPlayerId) return null;
    const found = playerStats.find((s) => s.playerId === selectedPlayerId);
    if (found) return found;
    // Build zero-value stats so the detail panel still renders
    const info = participatingPlayers.find(
      (p) => p.id === selectedPlayerId,
    );
    if (!info) return null;
    return {
      playerId: selectedPlayerId,
      playerName: info.short_name || info.full_name,
      jerseyNumber: info.jersey_number,
      teamSide: info.teamSide,
      totalEvents: 0,
      points: 0,
      fieldGoalsMade: 0,
      fieldGoalsAttempted: 0,
      threePointersMade: 0,
      threePointersAttempted: 0,
      freeThrowsMade: 0,
      freeThrowsAttempted: 0,
      rebounds: 0,
      assists: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      foulsCommitted: 0,
      foulsReceived: 0,
    } satisfies PlayerStats;
  }, [selectedPlayerId, playerStats, participatingPlayers]);

  /* Selected player heat map data */
  const selectedHeatData = useMemo(() => {
    if (!selectedPlayerId) return null;
    return computeHeatMapData(events, undefined, selectedPlayerId);
  }, [events, selectedPlayerId]);

  /* Selected player info */
  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) return null;
    return participatingPlayers.find((p) => p.id === selectedPlayerId) ?? null;
  }, [selectedPlayerId, participatingPlayers]);

  /* Split players by team for positioning */
  const homePlayers = participatingPlayers.filter(
    (p) => p.teamSide === "home",
  );
  const awayPlayers = participatingPlayers.filter(
    (p) => p.teamSide === "away",
  );

  return (
    <div
      className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-4"
      data-testid="player-analytics-view"
    >
      {/* Hint text */}
      <p className="text-xs text-slate-400 text-center">
        {t(
          "analytics.playerSelectHint",
          "Tap a player on the court to view individual stats",
        )}
      </p>

      {/* Clickable court with player dots */}
      <div className="flex justify-center">
        <svg
          viewBox={`-2 -2 ${COURT_W + 4} ${COURT_H + 4}`}
          className="w-full max-w-lg rounded-lg border border-slate-700 bg-amber-900 cursor-pointer"
          preserveAspectRatio="xMidYMid meet"
          data-testid="player-analytics-court"
        >
          {/* Court background */}
          <rect
            x={0}
            y={0}
            width={COURT_W}
            height={COURT_H}
            fill="#b5651d"
            rx={1}
          />

          {/* Court markings */}
          <g stroke="rgba(255,255,255,0.4)" strokeWidth={0.3} fill="none">
            <rect x={0} y={0} width={COURT_W} height={COURT_H} rx={0.5} />
            <line x1={COURT_W / 2} y1={0} x2={COURT_W / 2} y2={COURT_H} />
            <circle cx={COURT_W / 2} cy={COURT_H / 2} r={6} />
            {/* Paint areas */}
            <rect x={0} y={19} width={19} height={12} />
            <rect x={COURT_W - 19} y={19} width={19} height={12} />
          </g>

          {/* Home player dots */}
          {homePlayers.map((player, idx) => {
            const [cx, cy] = getPlayerPosition(
              { ...player, teamSide: "home" },
              idx,
            );
            const isSelected = selectedPlayerId === player.id;
            return (
              <g
                key={player.id}
                onClick={() =>
                  setSelectedPlayerId(isSelected ? null : player.id)
                }
                className="cursor-pointer"
                data-testid={`player-dot-${player.id}`}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={isSelected ? 3.5 : 2.5}
                  fill={isSelected ? "#f97316" : "#10b981"}
                  stroke={isSelected ? "#fff" : "rgba(255,255,255,0.4)"}
                  strokeWidth={isSelected ? 0.6 : 0.3}
                />
                <text
                  x={cx}
                  y={cy + 0.3}
                  fontSize={2.2}
                  fontWeight="bold"
                  fill="white"
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ pointerEvents: "none" }}
                >
                  {player.jersey_number}
                </text>
              </g>
            );
          })}

          {/* Away player dots */}
          {awayPlayers.map((player, idx) => {
            const [cx, cy] = getPlayerPosition(
              { ...player, teamSide: "away" },
              idx,
            );
            const isSelected = selectedPlayerId === player.id;
            return (
              <g
                key={player.id}
                onClick={() =>
                  setSelectedPlayerId(isSelected ? null : player.id)
                }
                className="cursor-pointer"
                data-testid={`player-dot-${player.id}`}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={isSelected ? 3.5 : 2.5}
                  fill={isSelected ? "#f97316" : "#6366f1"}
                  stroke={isSelected ? "#fff" : "rgba(255,255,255,0.4)"}
                  strokeWidth={isSelected ? 0.6 : 0.3}
                />
                <text
                  x={cx}
                  y={cy + 0.3}
                  fontSize={2.2}
                  fontWeight="bold"
                  fill="white"
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ pointerEvents: "none" }}
                >
                  {player.jersey_number}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Selected player detail section */}
      {selectedPlayer && selectedStats && selectedHeatData && (
        <div
          className="space-y-4 animate-in fade-in duration-200"
          data-testid="player-detail-panel"
        >
          {/* Player header */}
          <div className="flex items-center gap-3 bg-slate-900/60 rounded-lg p-3 border border-slate-700">
            <span
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold text-white ${
                selectedStats.teamSide === "home"
                  ? "bg-emerald-500"
                  : "bg-indigo-500"
              }`}
            >
              {selectedStats.jerseyNumber}
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-100">
                {selectedStats.playerName}
              </div>
              <div className="text-xs text-slate-400">
                {selectedStats.totalEvents}{" "}
                {t("analytics.playerEvents", "events")}
              </div>
            </div>
          </div>

          {/* Individual heat map */}
          <BasketballCourtHeatMap
            data={selectedHeatData}
            title={`${selectedStats.playerName} — ${t("analytics.heatMap", "Heat Map")}`}
            accentColor={
              selectedStats.teamSide === "home" ? "#10b981" : "#6366f1"
            }
            data-testid="player-heatmap"
          />

          {/* Stat cards grid */}
          <div
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2"
            data-testid="player-stat-cards"
          >
            <StatCard
              label={t("analytics.score", "Points")}
              value={selectedStats.points}
              testId="pstat-pts"
            />
            <StatCard
              label="FGM/FGA"
              value={`${selectedStats.fieldGoalsMade}/${selectedStats.fieldGoalsAttempted}`}
              testId="pstat-fg"
            />
            <StatCard
              label="3PM/3PA"
              value={`${selectedStats.threePointersMade}/${selectedStats.threePointersAttempted}`}
              testId="pstat-3p"
            />
            <StatCard
              label="FTM/FTA"
              value={`${selectedStats.freeThrowsMade}/${selectedStats.freeThrowsAttempted}`}
              testId="pstat-ft"
            />
            <StatCard
              label={t("actionRebound", "Rebounds")}
              value={selectedStats.rebounds}
              testId="pstat-reb"
            />
            <StatCard
              label={t("actionAssist", "Assists")}
              value={selectedStats.assists}
              testId="pstat-ast"
            />
            <StatCard
              label={t("actionSteal", "Steals")}
              value={selectedStats.steals}
              testId="pstat-stl"
            />
            <StatCard
              label={t("analytics.blocks", "Blocks")}
              value={selectedStats.blocks}
              testId="pstat-blk"
            />
            <StatCard
              label={t("actionTurnover", "Turnovers")}
              value={selectedStats.turnovers}
              testId="pstat-to"
            />
            <StatCard
              label={t("analytics.foulsCommitted", "Fouls")}
              value={selectedStats.foulsCommitted}
              testId="pstat-fc"
            />
            <StatCard
              label={t("analytics.foulsReceived", "Fouls Recv")}
              value={selectedStats.foulsReceived}
              testId="pstat-fr"
            />
            <StatCard
              label={t("analytics.totalEvents", "Total Events")}
              value={selectedStats.totalEvents}
              testId="pstat-evt"
            />
          </div>
        </div>
      )}

      {/* Empty state when no player selected */}
      {!selectedPlayer && (
        <div
          className="text-center text-slate-500 text-sm py-6"
          data-testid="player-analytics-empty"
        >
          {t("analytics.noPlayerEvents", "Select a player to view their stats")}
        </div>
      )}
    </div>
  );
};

export default PlayerAnalyticsView;
